import { WatchProductSummarySchema } from '@/lib/service/ecs/contracts';
import type { WatchProductSummary } from '@/lib/service/ecs/contracts';
import { devError } from '@/lib/utils/devLog';

const WATCH_DETAILS_GENERIC_FAILURE = 'WATCH_DETAILS_GENERIC_FAILURE';

export interface WatchDetailsQuery {
  countryCode: string;
  locale: string;
  watchReference: string;
}

function isRecord(value: unknown): value is Record<string, unknown> {
  return typeof value === 'object' && value !== null;
}

function readErrorMessage(json: unknown): string {
  if (!isRecord(json) || !Array.isArray(json.errors)) return '';
  const first = json.errors[0];
  if (isRecord(first) && typeof first.message === 'string' && first.message) return first.message;
  return '';
}

export function buildWatchDetailsUrl(query: WatchDetailsQuery): string {
  const params = new URLSearchParams({
    countryCode: query.countryCode,
    locale: query.locale,
    watchReference: query.watchReference.trim(),
  });
  return `/api/service/watch-details?${params.toString()}`;
}

export async function fetchWatchDetails(query: WatchDetailsQuery): Promise<WatchProductSummary> {
  let response: Response;
  try {
    response = await fetch(buildWatchDetailsUrl(query));
  } catch (error) {
    devError('[watchDetailsClient] request failed:', error);
    throw new Error(WATCH_DETAILS_GENERIC_FAILURE, { cause: error });
  }

  const json: unknown = await response.json().catch(() => null);
  if (!response.ok || !isRecord(json) || json.success !== true) {
    throw new Error(readErrorMessage(json) || WATCH_DETAILS_GENERIC_FAILURE);
  }

  const parsed = WatchProductSummarySchema.safeParse(json.data);
  if (!parsed.success) throw new Error(WATCH_DETAILS_GENERIC_FAILURE);
  return parsed.data;
}
